import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Button, Input } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import Header from "../../layout/Header/Header";
import { hoyLocal } from "../../fechaLocal";

// Tablero del ejecutivo: que atender hoy, ventas, cobranza, cuentas por pagar,
// inventario, pedidos y lo que viene en camino. Cada bloque lleva a su reporte.
// Pedidos y pendiente de recibir salen de los mismos calculos que sus reportes.
// Las graficas son de una sola serie: un solo color y sin leyenda.
const SERIE = "#2a78d6";
const REJILLA = "#e2e8f0";
const ESTADO = {
  bien: { color: "#15803d", fondo: "#f0fdf4", icono: "fas fa-check-circle", etiqueta: "Listo" },
  atencion: { color: "#b45309", fondo: "#fffbeb", icono: "fas fa-exclamation-triangle", etiqueta: "Atención" },
  critico: { color: "#dc2626", fondo: "#fef2f2", icono: "fas fa-exclamation-circle", etiqueta: "Urgente" },
};
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Math.round(Number(n) || 0));
const dinero = (n) => `$${num(n)}`;
const etiquetaMes = (m) => `${MESES[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;

const PERIODOS = {
  mes: { texto: "Este mes", rango: (h) => [moment(h).startOf("month"), moment(h)] },
  pasado: { texto: "Mes pasado", rango: (h) => [moment(h).subtract(1, "month").startOf("month"), moment(h).subtract(1, "month").endOf("month")] },
  dias90: { texto: "90 días", rango: (h) => [moment(h).subtract(89, "days"), moment(h)] },
  anio: { texto: "Este año", rango: (h) => [moment(h).startOf("year"), moment(h)] },
};

function Bloque({ titulo, href, enlace, children, ancho }) {
  return (
    <section style={{ gridColumn: ancho ? `span ${ancho}` : undefined, minWidth: 0, border: `1px solid ${REJILLA}`, borderRadius: 8, padding: "12px 14px", background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <h5 style={{ margin: 0, fontSize: 15 }}>{titulo}</h5>
        {href ? <a href={href} style={{ fontSize: 12, whiteSpace: "nowrap" }}>{enlace || "Ver reporte"}</a> : null}
      </div>
      {children}
    </section>
  );
}

function Tile({ titulo, valor, detalle, href, color }) {
  return (
    <a href={href} style={{ display: "block", padding: "12px 14px", border: `1px solid ${REJILLA}`, borderRadius: 8, background: "#fff", color: "inherit", textDecoration: "none", minWidth: 0 }}>
      <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>{titulo}</div>
      <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: color || "#0f172a", whiteSpace: "nowrap" }}>{valor}</div>
      <div style={{ fontSize: 12, color: "#64748b" }}>{detalle}</div>
    </a>
  );
}

// Barras horizontales con su etiqueta y su valor siempre visibles
function Barras({ filas, formato, vacio }) {
  if (!filas.length) return <p style={{ color: "#94a3b8", margin: 0 }}>{vacio}</p>;
  const max = Math.max(...filas.map((f) => f.valor), 1);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {filas.map((f) => (
        <div key={f.nombre} title={f.detalle || undefined}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nombre}</span>
            <b style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formato(f.valor)}</b>
          </div>
          <div style={{ height: 8, background: "#f1f5f9", borderRadius: 4 }}>
            {f.valor > 0 ? <div style={{ width: `${Math.max(1, (f.valor / max) * 100)}%`, height: 8, borderRadius: 4, background: SERIE }} /> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

// Columnas por mes: el renglon sensible al paso del mouse es toda la altura, no la
// barra, para poder leer tambien los meses chicos.
function VentasPorMes({ meses }) {
  const [activo, setActivo] = useState(null);
  const max = Math.max(...meses.map((m) => m.importe), 1);
  const mostrado = activo === null ? null : meses[activo];
  const conVenta = meses.filter((m) => m.importe > 0);
  return (
    <div>
      <div style={{ minHeight: 20, fontSize: 13, color: "#475569", marginBottom: 4 }}>
        {mostrado
          ? <><b>{etiquetaMes(mostrado.mes)}</b>: {dinero(mostrado.importe)} · {num(mostrado.piezas)} piezas · {num(mostrado.notas)} nota(s)</>
          : conVenta.length ? "Pasa el mouse por un mes para ver el detalle." : "Sin ventas en los últimos 12 meses."}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${meses.length}, 1fr)`, gap: 4, height: 170, alignItems: "end", borderBottom: "1px solid #cbd5e1" }}>
        {meses.map((m, i) => (
          <div key={m.mes} onMouseEnter={() => setActivo(i)} onMouseLeave={() => setActivo(null)}
            style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", background: activo === i ? "#f1f5f9" : "transparent", borderRadius: "4px 4px 0 0" }}>
            {m.importe > 0 && m.importe === max ? <span style={{ fontSize: 11, fontWeight: 700, color: "#334155", whiteSpace: "nowrap" }}>{dinero(m.importe)}</span> : null}
            {m.importe > 0 ? <div style={{ width: "62%", maxWidth: 34, height: `${Math.max(2, (m.importe / max) * 88)}%`, background: SERIE, borderRadius: "4px 4px 0 0" }} /> : null}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${meses.length}, 1fr)`, gap: 4, marginTop: 4 }}>
        {meses.map((m, i) => (
          <div key={m.mes} style={{ textAlign: "center", fontSize: 10, lineHeight: 1.2, color: "#64748b" }}>
            {MESES[Number(m.mes.slice(5, 7)) - 1]}
            {/* el año solo donde empieza, para que quepa en doce columnas */}
            {i === 0 || m.mes.endsWith("-01") ? <div>{m.mes.slice(0, 4)}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardEjecutivo() {
  const { user } = useContext(AuthContext);
  const hoy = hoyLocal();
  const [periodo, setPeriodo] = useState("mes");
  const [inicio, setInicio] = useState(moment(hoy).startOf("month").format("YYYY-MM-DD"));
  const [fin, setFin] = useState(hoy);
  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!user.menu_reportes || !inicio || !fin || inicio > fin) return;
    setCargando(true);
    axios.get(`${process.env.REACT_APP_URL_SEGUIMIENTOS}Ejecutivo/${inicio}/${fin}`, { headers: headers() })
      .then((r) => { setD(r.data); setCargando(false); })
      .catch((e) => {
        setCargando(false);
        Swal.fire({ icon: "error", title: "No se pudo cargar el tablero", text: e.response?.data?.message || e.message });
      });
  }, [inicio, fin, user.menu_reportes]);

  const elige = (k) => {
    const [a, b] = PERIODOS[k].rango(hoy);
    setPeriodo(k); setInicio(a.format("YYYY-MM-DD")); setFin(b.format("YYYY-MM-DD"));
  };

  const cambioVentas = () => {
    const antes = d.ventas.anterior.importe;
    if (!antes) return `${num(d.ventas.piezas)} pzas · sin ventas el periodo anterior`;
    const pct = Math.round(((d.ventas.importe - antes) / antes) * 100);
    return `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct)}% vs periodo anterior · ${num(d.ventas.piezas)} pzas`;
  };

  if (!user.menu_reportes) return <Header />;

  return (
    <>
      <Header />
      <br />
      <br />
      <div className="card container col-12" style={{ background: "#f8fafc" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap", margin: "10px 0 12px" }}>
          <div>
            <h3 style={{ margin: 0 }}>Dashboard ejecutivo</h3>
            <div style={{ color: "#64748b", fontSize: 13 }}>
              Periodo {inicio} a {fin}{cargando ? " · actualizando..." : ""}. Pedidos, inventario y saldos son al día de hoy.
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {Object.entries(PERIODOS).map(([k, p]) => (
              <Button key={k} size="sm" color={periodo === k ? "primary" : "secondary"} outline={periodo !== k} onClick={() => elige(k)}>{p.texto}</Button>
            ))}
            <Input bsSize="sm" type="date" style={{ width: 140 }} value={inicio} onChange={(e) => { setPeriodo(""); setInicio(e.target.value); }} />
            <Input bsSize="sm" type="date" style={{ width: 140 }} value={fin} onChange={(e) => { setPeriodo(""); setFin(e.target.value); }} />
          </div>
        </div>

        {!d ? <p style={{ color: "#94a3b8" }}>Cargando...</p> : (
          <>
            {d.alertas.length ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Qué atender hoy</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 8 }}>
                  {d.alertas.map((a) => {
                    const e = ESTADO[a.nivel];
                    return (
                      <a key={a.texto} href={a.href} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 12px", borderRadius: 8, background: e.fondo, color: "#0f172a", textDecoration: "none", border: `1px solid ${REJILLA}` }}>
                        <i className={e.icono} style={{ color: e.color, marginTop: 3 }} aria-hidden="true" />
                        <span style={{ fontSize: 13 }}>
                          <b style={{ color: e.color }}>{e.etiqueta}:</b> <b>{a.moneda ? dinero(a.valor) : num(a.valor)}</b> {a.texto}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10, marginBottom: 12 }}>
              <Tile titulo="Ventas del periodo" valor={dinero(d.ventas.importe)} detalle={cambioVentas()} href="/DashboardVentas" />
              <Tile titulo="Cobrado del periodo" valor={dinero(d.cobranza.cobrado)} detalle="Abonos de clientes" href="/DashboardCobranza" />
              <Tile titulo="Por cobrar" valor={dinero(d.cobranza.porCobrar)} detalle={`${dinero(d.cobranza.vencido)} vencido`} href="/DashboardCobranza" />
              <Tile titulo="Por pagar (USD)" valor={dinero(d.cxp.saldo)} detalle={`${num(d.cxp.notas)} nota(s) con saldo`} href="/DashboardCXP" />
              <Tile titulo="Inventario" valor={`${num(d.inventario.piezas)} pzas`} detalle={`${num(d.inventario.modelos)} modelos con existencia`} href="/ListadoInventarios" />
              <Tile titulo="Por surtir" valor={`${num(d.pedidos.porSurtir)} pzas`} detalle={`${num(d.pedidos.abiertos)} pedidos · ${num(d.pedidos.prepedidos)} prepedidos`} href="/SeguimientoPedidos" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 10, marginBottom: 20 }}>
              <Bloque titulo="Ventas por mes" href="/DashboardVentas"><VentasPorMes meses={d.ventas.porMes} /></Bloque>
              <Bloque titulo="Principales clientes del periodo" href="/DashboardVentas">
                <Barras vacio="Sin ventas en el periodo." formato={dinero}
                  filas={d.topClientes.filter((c) => c.importe > 0).map((c) => ({ nombre: c.nombre, valor: c.importe, detalle: `${num(c.piezas)} piezas` }))} />
              </Bloque>
              <Bloque titulo="Vendedores del periodo" href="/DashboardVentas">
                <Barras vacio="Sin ventas en el periodo." formato={dinero}
                  filas={d.topVendedores.filter((c) => c.importe > 0).map((c) => ({ nombre: c.nombre, valor: c.importe, detalle: `${num(c.piezas)} piezas` }))} />
              </Bloque>
              <Bloque titulo="Antigüedad de lo que nos deben" href="/DashboardCobranza">
                <Barras vacio="No hay saldos por cobrar." formato={dinero}
                  filas={d.cobranza.antiguedad.map((a) => ({ nombre: a.rango, valor: a.importe }))} />
              </Bloque>
              <Bloque titulo="Pedidos por estado" href="/SeguimientoPedidos">
                <Barras vacio="No hay pedidos abiertos." formato={(v) => `${num(v)} ped.`}
                  filas={d.pedidos.porEstado.map((e) => ({ nombre: e.estado, valor: e.pedidos, detalle: `${num(e.piezas)} piezas por surtir` }))} />
              </Bloque>
              <Bloque titulo="Lo que viene en camino" href="/PendienteRecibir">
                <div style={{ display: "flex", gap: 20, marginBottom: 8, fontSize: 13 }}>
                  <span>En tránsito <b>{num(d.recibir.transito)}</b></span>
                  <span>Por embarcar <b>{num(d.recibir.porEmbarcar)}</b></span>
                </div>
                {d.recibir.llegadas.length ? d.recibir.llegadas.map((l) => (
                  <div key={`${l.documento}${l.fecha}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, padding: "4px 0", borderTop: `1px solid ${REJILLA}` }}>
                    <span>{l.documento}<span style={{ color: "#64748b" }}> · ETA {l.fecha || "sin fecha"}</span></span>
                    <b>{num(l.piezas)}</b>
                  </div>
                )) : <p style={{ color: "#94a3b8", margin: 0 }}>No hay embarques en tránsito.</p>}
              </Bloque>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default DashboardEjecutivo;
